import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import OppCombobox from '../App/OppCombobox';
import useDebounce from '../../hooks/useDebounce';
import PillJourney from './PillJourney';
import {
  Modal,
  Button,
  MediaObject,
  Icon,
  Checkbox,
  Tooltip,
  Input,
} from '@salesforce/design-system-react';
import CollaboratorComboBox from '../App/CollaboratorComboBox';

const JourneyModal = (props) => {
  const { onChange, onCollaboratorsChange } = props;
  const [searchTerm, setSearchTerm] = useState('');
  const [collaboratorSearchTerm, setCollaboratorSearchTerm] = useState('');
  const [oppId, setOppId] = useState('');
  const [collaborators, setCollaborators] = useState([]);
  const [checked, setChecked] = useState(false);
  const [journeyName, setJourneyName] = useState('');
  const [industryName, setIndustryName] = useState('');
  const [pillOptions, setPillOptions] = useState([]);

  // Now we call our hook, passing in the current searchTerm value.
  // The hook will only return the latest value (what we passed in) ...
  // ... if it's been more than 500ms since it was last called.
  // Otherwise, it will return the previous value of searchTerm.
  // The goal is to only have the API call fire when user stops typing ...
  // ... so that we aren't hitting our API rapidly.
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedCollaboratorSearchTerm = useDebounce(
    collaboratorSearchTerm,
    500
  );

  // Here's where the API call happens
  // We use useEffect since this is an asynchronous action
  useEffect(
    () => {
      // Make sure we have a value (user has entered something in input)
      if (debouncedSearchTerm) {
        // Fire off our API call
        onChange(debouncedSearchTerm);
      }

      if (debouncedCollaboratorSearchTerm) {
        onCollaboratorsChange(debouncedCollaboratorSearchTerm);
      }
    },
    // This is the useEffect input array
    // Our useEffect function will only execute if this value changes ...
    // ... and thanks to our hook it will only change if the original ...
    // value (searchTerm) hasn't changed for more than 500ms.

    // eslint-disable-next-line
    [debouncedSearchTerm, debouncedCollaboratorSearchTerm]
  );

  return (
    <Modal
      footer={[
        <Button
          key="cancelBtn"
          variant="neutral"
          label="Cancel"
          onClick={props.toggleOpen}
        />,
        <Button
          key="promptBtn"
          variant="brand"
          label="Start Journey"
          disabled={!((oppId !== '' || checked === true) && journeyName !== '')}
          onClick={() => {
            props.onSubmit({
              oppId,
              checked,
              journeyName,
              industryName,
              collaborators,
              pillOptions,
            });
          }}
        />,
      ]}
      isOpen={props.isOpen}
      onRequestClose={props.toggleOpen}
      size="small"
      heading={<span>New Hero Journey</span>}
      style={{ backgroundColor: '#eee' }}
    >
      <div
        className="slds-m-around_medium slds-p-around_medium"
        style={{
          backgroundColor: '#f3f3f3',
          border: '1px solid #dddbda',
          borderRadius: '.25rem',
        }}
      >
        <MediaObject
          body={
            <span>
              <strong>Step 1: &nbsp;</strong>Name Your Journey
            </span>
          }
          figure={
            <Icon
              category="standard"
              containerStyle={{
                backgroundColor: '#027e46',
              }}
              name="opportunity"
              size="medium"
            />
          }
          verticalCenter
        />
        <div className="slds-m-vertical_x-small">
          <Input
            label="Journey Name"
            required
            value={journeyName}
            onChange={(e) => setJourneyName(e.target.value)}
          />
        </div>
      </div>
      <div
        className="slds-m-around_medium slds-p-around_medium"
        style={{
          backgroundColor: '#f3f3f3',
          border: '1px solid #dddbda',
          borderRadius: '.25rem',
        }}
      >
        <MediaObject
          body={
            <span>
              <strong>Step 2: &nbsp;</strong>
              Relate your Hero Journey to an Opportunity
            </span>
          }
          figure={
            <Icon
              category="standard"
              containerStyle={{
                backgroundColor: '#fcb95b',
              }}
              q
              name="opportunity"
              size="medium"
            />
          }
          verticalCenter
        />
        <div className="slds-m-vertical_x-small">
          <OppCombobox
            options={[...props.opps]}
            inputValue={searchTerm}
            isLoading={props.isLoading}
            onChange={(e) => setSearchTerm(e)}
            onSelect={(e, value) => {
              setOppId(value[0].id);
              setIndustryName(value[0].industry);
            }}
          />
        </div>
        <div
          className="slds-box slds-theme_shade slds-m-vertical_medium"
          style={{ backgroundColor: '#fff' }}
        >
          <Checkbox
            assistiveText={{
              label: 'Default',
            }}
            checked={checked}
            labels={{
              label: 'This Hero Journey is not related to any opportunity.',
            }}
            onChange={(e) => setChecked(e.target.checked)}
          />
        </div>
      </div>
      <div
        className="slds-m-around_medium slds-p-around_medium"
        style={{
          backgroundColor: '#f3f3f3',
          border: '1px solid #dddbda',
          borderRadius: '.25rem',
        }}
      >
        <MediaObject
          body={
            <div>
              <span>
                <strong>Optional: &nbsp;</strong>
                Add Collaborators to Your Journey
              </span>
              &nbsp;
              <Tooltip
                align="right"
                content="Note: Full email addresses are needed to search a User. Collaborators will be given Read-Only access to your journey and will be able to generate Quip and Google Slide outputs. If you do not find a user, send them an email using the 'Invite Collaborators by Email' option"
                variant="learnMore"
              />
            </div>
          }
          figure={
            <Icon
              category="custom"
              containerStyle={{
                backgroundColor: 'rgba(0, 112, 210, 0.4)',
              }}
              name="custom103"
              size="medium"
            />
          }
          verticalCenter
        />

        <div className="slds-m-vertical_medium">
          <CollaboratorComboBox
            options={[...props.collaborator]}
            inputValue={collaboratorSearchTerm}
            isLoading={props.isLoadingCollaborator}
            onChange={(e) => setCollaboratorSearchTerm(e)}
            setCollaborators={(value) => setCollaborators(value)}
          ></CollaboratorComboBox>

          <div className="slds-m-vertical_medium slds-align-absolute_center slds-text-title_caps">
            - OR -
          </div>

          <PillJourney
            pillOptions={pillOptions}
            journeyEmail={props.journeyEmail}
            setPillOptions={(value) => setPillOptions(value)}
          />
        </div>
      </div>
    </Modal>
  );
};

JourneyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default JourneyModal;
